import { SHeaderSortable, STableContainer } from "@avalara/skylab-sdk/react";
import classnames from "classnames";
import React from "react";
import NoData from "../../features/search/NoData";
import RecordCount from "../../features/search/RecordCount";
import { DataGridProps } from "./types";

// Type declarations for legacy JSX components
const TypedRecordCount = RecordCount as React.ComponentType<{
    recordCount: number;
    recordName: string;
    selectedCount: number;
    hasFilterChips?: boolean;
    usesFilterChips?: boolean;
}>;

const TypedNoData = NoData as React.ComponentType<{
    showGenNoDataMsg?: boolean;
    errorMessage?: string;
}>;

function DataGrid<T extends { id: number | string }>({
    gridHeaders,
    gridData,
    sortFunction,
    gridName,
    totalRecords,
    sortColumn,
    hasFilterChips = false,
    usesFilterChips = false,
    scrollable = false,
    scrollableClassName = "",
    onScroll,
    errorMessage,
    testId,
}: DataGridProps<T>): React.JSX.Element {
    // Show error state if errorMessage is provided
    if (errorMessage) {
        return <TypedNoData showGenNoDataMsg={false} errorMessage={errorMessage} />;
    }

    // Show empty state if no data
    if (!gridData || gridData.length === 0) {
        return <TypedNoData showGenNoDataMsg />;
    }

    const tableClass = classnames({
        "scrollable-grid": scrollable,
        "top-negative-20": hasFilterChips,
        [scrollableClassName]: !!scrollableClassName,
    });

    const tableContent = (
        <STableContainer>
            <table>
                <thead>
                    <tr>
                        {gridHeaders?.map(header => (
                            <th key={header.id}>
                                {header.sortable ? (
                                    <SHeaderSortable
                                        text={header.label || ""}
                                        ascending={sortColumn[1]}
                                        descending={sortColumn[0] === header.id && !sortColumn[1]}
                                        active={sortColumn[0] === header.id}
                                        onS-sort={() => sortFunction(header.id)}
                                    />
                                ) : (
                                    header.label || ""
                                )}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {gridData?.map(rowData => (
                        <tr key={rowData.id}>
                            {gridHeaders.map(header => (
                                <td key={header.id}>
                                    {header.render
                                        ? (header.render(rowData) as React.ReactNode)
                                        : String(
                                              (rowData as Record<string, unknown>)[header.id] ?? ""
                                          )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </STableContainer>
    );

    return (
        <>
            <TypedRecordCount
                recordCount={totalRecords}
                recordName={gridName}
                selectedCount={0}
                hasFilterChips={hasFilterChips}
                usesFilterChips={usesFilterChips}
            />
            {scrollable ? (
                <div className={tableClass} data-testid={testId} onScroll={onScroll}>
                    {tableContent}
                </div>
            ) : (
                <div data-testid={testId}>{tableContent}</div>
            )}
        </>
    );
}

export default DataGrid;
